/* istanbul ignore file */
"use client";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarCustomInput,
} from "@/shared/components/ui/sidebar";
import { useEffect, useRef, useState, useCallback } from "react";
import { CircleX, Search, X } from "lucide-react";
import SpecificationSidebarViewModel from "./specification-sidebar-view-model";
import { SpecificationCategoryList } from "./category-list/category-list";
import { Input } from "@/shared/components/ui/input";
import Logo from "@/shared/components/logo/logo";
import {
  setIsInputFocused,
  setLoading,
  setOpenCategoriesCollapse,
  setSearchTerm,
  toggleSidebarFilterConflicts,
  toggleSidebarFilterStatus,
} from "@/core/redux/slices/specification";
import { useSelector } from "@/core/redux/store";
import { Typography } from "@/shared/components/ui/typography";
import { getCategoryAndItemThunk } from "@/core/redux/thunks/specificationThunk";
import { debounce } from "lodash";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { SidebarFilterDropdown } from "./sidebar-filter-dropdown";
import ToggleSidebarIcon from "@/assets/icons/toggle-sidebar.svg";
import { SpecificationItemAnswerStatus } from "@/shared/enums/specification-item-answer";

export function SpecificationSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { t, specification, categories, setName, dispatch, handleChangeName } =
    SpecificationSidebarViewModel();
  const me = useSelector((state) => state.user.me);
  const supplier = useSelector(
    (state) => state.productAnswer.supplierHashResponse?.supplier
  );
  const sidebarWidth = useSelector(
    (state) => state.specificationList.sidebarWidth
  );
  const isSurveyRespondent = !!supplier?.id;
  const sidebarFilter =
    useSelector((state) => state.specification.sidebarFilter) ??
    ({ answerStatuses: [], showConflicts: false } as const);

  const [firstLoad, setFirstLoad] = useState(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
    // Wait for input to render before focusing.
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);

  const handleToggleAllCategories = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const shouldExpand = specification.openCategories.length === 0;
      dispatch(setOpenCategoriesCollapse(shouldExpand));
    },
    [dispatch, specification.openCategories.length]
  );

  const debouncedSearch = useCallback(
    debounce(async (search: string) => {
      if (!me?.company.id) return;
      const response = await dispatch(
        getCategoryAndItemThunk({
          specificationId: specification.specification.specification.id,
          companyId: me?.company.id,
          search: search || "",
          userId: me?.id || undefined,
        })
      );

      dispatch(setSearchTerm(search));
      if (response) {
        setTimeout(() => {
          dispatch(setLoading(false));
        }, 1000);
      }
    }, 800),
    [me?.company.id, specification.specification.specification.id]
  );

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setIsSearchFocused(false);
    setSearchValue("");
    dispatch(setSearchTerm(""));
    dispatch(setLoading(true));
    debouncedSearch("");
  }, [debouncedSearch, dispatch]);

  const hasActiveFilter =
    sidebarFilter.answerStatuses.length > 0 || sidebarFilter.showConflicts;

  const activeFilterBadges = useCallback(() => {
    if (!hasActiveFilter) return [];

    const order: SpecificationItemAnswerStatus[] = [
      SpecificationItemAnswerStatus.ANSWERED_YES,
      SpecificationItemAnswerStatus.ANSWERED_NO,
      SpecificationItemAnswerStatus.ANSWERED_PARTIALLY,
      SpecificationItemAnswerStatus.NOT_FOUND,
    ];

    const statusLabels: Partial<Record<SpecificationItemAnswerStatus, string>> = {
      [SpecificationItemAnswerStatus.ANSWERED_YES]: t("filter-yes"),
      [SpecificationItemAnswerStatus.ANSWERED_NO]: t("filter-no"),
      [SpecificationItemAnswerStatus.ANSWERED_PARTIALLY]: t("filter-partially"),
      [SpecificationItemAnswerStatus.NOT_FOUND]: t("filter-not-found"),
    };

    const badges: Array<{ key: string; label: string; onClear: () => void }> =
      [];

    const selectedStatuses = new Set(sidebarFilter.answerStatuses);
    for (const s of order) {
      if (selectedStatuses.has(s)) {
        badges.push({
          key: `status-${s}`,
          label: statusLabels[s] ?? t("filter-answer-status"),
          onClear: () => dispatch(toggleSidebarFilterStatus(s)),
        });
      }
    }

    if (sidebarFilter.showConflicts) {
      badges.push({
        key: "conflicts",
        label: t("filter-conflicts"),
        onClear: () => dispatch(toggleSidebarFilterConflicts()),
      });
    }

    return badges;
  }, [
    dispatch,
    hasActiveFilter,
    sidebarFilter.answerStatuses,
    sidebarFilter.showConflicts,
    t,
  ]);

  return (
    <div
      className="group peer hidden md:block"
      style={{ width: `${sidebarWidth}px` }}
    >
      <div
        className="fixed inset-y-0 left-0 z-10 h-svh"
        style={{ width: `${sidebarWidth}px` }}
      >
        <Sidebar
          collapsible="none"
          className="h-full w-full flex flex-col bg-sidebar text-sidebar-foreground"
          {...props}
        >
          <SidebarHeader className="gap-1 w-full">
            {/* Logo stays visible even when search is open */}
            <div className="px-2.5 py-2">
              <Logo style={{ height: "22px" }} />
            </div>

            {!isSearchOpen ? (
              <>
                {/* Specification name + actions */}
                <div className="flex w-full items-center gap-1 min-w-0">
                  {isSurveyRespondent ? (
                    <div className="flex-1 min-w-0 sidebar-input-title text-zinc-950 font-600 h-[35px] bg-transparent border border-transparent px-3 flex items-center rounded-md cursor-default select-none">
                      <span className="text-sm font-600 text-zinc-950 truncate whitespace-nowrap overflow-hidden">
                        {specification.specification.specification.name ||
                          t("namelessSpecification")}
                      </span>
                    </div>
                  ) : (
                    <Input
                      className="flex-1 min-w-0 sidebar-input-title text-zinc-950 font-600 h-[35px] bg-transparent border-none whitespace-nowrap overflow-hidden
                    focus:bg-white focus:rounded-md focus:border focus:border-input focus:ring-0
                    px-3 focus:py-0 focus:shadow-none focus:font-600 md:focus:text-sm"
                      value={specification.specification.specification.name}
                      placeholder={t("namelessSpecification")}
                      onFocus={(e) => {
                        dispatch(setIsInputFocused(true));
                        e.target.select();
                      }}
                      onBlur={() => dispatch(setIsInputFocused(false))}
                      onChange={(e) => {
                        setName(e.target.value);
                        handleChangeName(e.target.value);
                      }}
                    />
                  )}

                  {/* Actions: always visible */}
                  <div className="ml-auto flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      aria-label="Open search"
                      className="flex items-center justify-center w-[36px] h-[36px] rounded-md bg-transparent hover:bg-[#E4E4E7] transition-colors flex-shrink-0"
                      onClick={openSearch}
                    >
                      <Search
                        width={16}
                        height={16}
                        className="text-zinc-700"
                      />
                    </button>
                    <SidebarFilterDropdown />
                  </div>
                </div>

                {/* Active filter badges under specification name */}
                {hasActiveFilter && (
                  <div className="flex w-full items-center px-3">
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {activeFilterBadges().map((badge) => (
                        <div
                          key={badge.key}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-[#FFFFFF] px-2 py-1"
                          aria-label="active-filter-badge"
                        >
                          <span className="text-[11px] font-600 text-[#3F3F46] whitespace-nowrap">
                            {badge.label}
                          </span>
                          <button
                            type="button"
                            aria-label={`clear-active-filter-${badge.key}`}
                            className="flex items-center justify-center rounded-full hover:bg-[#E4E4E7] text-[#71717A]"
                            onClick={badge.onClear}
                          >
                            <CircleX width={14} height={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Search open: input fills available space, only close icon shown
              <div className="flex items-center gap-1 w-full">
                <SidebarCustomInput
                  ref={searchInputRef}
                  value={searchValue}
                  leftElement={
                    !firstLoad && specification.specificationLoading ? (
                      <DotLottieReact
                        src="/animations/lottie/loading-spinner.json"
                        style={{
                          width: "35px",
                          height: "35px",
                          marginLeft: "-10px",
                        }}
                        autoplay={true}
                        loop={true}
                      />
                    ) : (
                      <Search width={16} height={16} className="-mt-[1px]" />
                    )
                  }
                  onFocus={() => {
                    dispatch(setIsInputFocused(true));
                    setIsSearchFocused(true);
                  }}
                  onBlur={() => {
                    dispatch(setIsInputFocused(false));
                    setIsSearchFocused(false);
                  }}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    dispatch(setLoading(true));
                    debouncedSearch(e.target.value);
                  }}
                  placeholder={isSearchFocused ? "" : t("search")}
                  className="h-[36px] font-600 flex-1 min-w-0"
                />
                <button
                  type="button"
                  aria-label="Close search"
                  className="ml-auto flex items-center justify-center w-[36px] h-[36px] rounded-md bg-transparent hover:bg-[#E4E4E7] transition-colors flex-shrink-0"
                  onClick={closeSearch}
                >
                  <X width={18} height={18} className="text-zinc-700" />
                </button>
              </div>
            )}
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                {!specification.skeletonLoading && (
                  <div className="mb-1 py-5 px-2 border-t border-[#E5E7EB]">
                    <div className="categories-title font-600 flex items-center justify-between">
                      <Typography variant="bodyMuted" className="font-600">
                        {t("categories")}
                      </Typography>
                      {!isSearchOpen && (
                        <button
                          type="button"
                          data-testid="toggle-all-categories"
                          aria-label="Toggle all categories"
                          className="h-6 w-6 flex items-center justify-center rounded-md bg-transparent hover:bg-[#E4E4E7] transition-colors"
                          onClick={handleToggleAllCategories}
                        >
                          <img
                            src={ToggleSidebarIcon}
                            alt="toggle all categories"
                            width={26}
                            height={26}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <SpecificationCategoryList categories={categories} />
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
      </div>
    </div>
  );
}
